import type {CopilotMessageMetadata, CopilotUIMessage} from '@mixpanel/ai-copilot/agent/types';
import type {ChatStatus} from 'ai';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import type {LocalizationStore} from 'common/i18n/store';
import {getProjectId, getReportName, getReportUrl, getWorkspaceId} from 'common/report/util';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {StateFromUrlUtils} from 'common/report/util/state-from-url';
import {ReportName} from 'common/report/constants';
import type {ConversationSummary} from 'common/copilot/conversations-api';
import {MAX_CONVERSATION_TITLE_LENGTH} from 'common/copilot/conversations-api';

export const COPILOT_CHAT_VIEW_QUERY_PARAM = `cp_chat_view`;
// Only meaningful for the panel surface — the full-page surface gets the id from the route param.
export const COPILOT_CONVERSATION_ID_QUERY_PARAM = `cp_conversation_id`;
export const COPILOT_SHARED_NUM_MESSAGES_QUERY_PARAM = `cp_num_messages`;
// Deep-link param: landing on the agent with this set auto-submits a canned
// first message on the user's behalf (see CHAT_INTENT_PROMPT). One-shot — the
// param is stripped from the URL after firing so a refresh doesn't resubmit.
export const COPILOT_CHAT_INTENT_QUERY_PARAM = `chat_intent`;
// Set by the visual-editor CTA when navigating to the experiment page; the
// Edit-on-Site button reads it to auto-open its URL-input modal, then strips it.
export const OPEN_OVERLAY_QUERY_PARAM = `open_overlay`;

export enum CopilotChatIntent {
  // From the in-product onboarding flow: "help me get set up".
  UserOnboarding = `user_onboarding`,
}

// The model-facing message auto-submitted for each known intent. Kept in code (not
// the URL) so the deep link can't inject arbitrary prompts on the user's behalf.
// The onboarding kickoff is never shown as a user bubble; the UI renders a greeting
// in its place, and the server-side onboarding guidance tells the agent not to greet again.
export const CHAT_INTENT_PROMPT: Record<CopilotChatIntent, string> = {
  [CopilotChatIntent.UserOnboarding]: `Help me get set up`,
};

// Pull the user's first name from the identity cookie, handling the
// "Last, First" cookie format in addition to "First Last".
export function getCurrentUserFirstName(): string | null {
  const fullName = userCookieUtils.getUserIdentityFromCookie()?.name?.trim();
  if (!fullName) {
    return null;
  }
  const candidate = fullName.includes(`,`) ? fullName.split(`,`)[1]?.trim() : fullName;
  return candidate?.split(/\s+/)[0] || null;
}

// A friendlier title for an intent-started chat, applied after the first message
// is submitted so the history entry reads better than the canned prompt. Returns
// undefined for intents with no custom title (title then falls back to the first
// message). Takes the localization store so the label and date are localized.
export function chatIntentTitle(intent: CopilotChatIntent, i18n: LocalizationStore): string | undefined {
  if (intent === CopilotChatIntent.UserOnboarding) {
    // Locale-aware short date, e.g. "Jul 29, 2026".
    const date = i18n.formatDate(new Date(), {year: `numeric`, month: `short`, day: `numeric`});
    return i18n.t(`copilot.onboarding.chat-title`, {date}).slice(0, MAX_CONVERSATION_TITLE_LENGTH);
  }
  return undefined;
}

// Metadata stamped on an intent's auto-submitted kickoff message, read at submit time so the
// greeting name is the user who started the chat. Returns undefined for intents whose kickoff
// is an ordinary user message.
export function chatIntentKickoffMetadata(intent: CopilotChatIntent): CopilotMessageMetadata | undefined {
  if (intent === CopilotChatIntent.UserOnboarding) {
    return {systemEvent: `onboarding_kickoff`, onboardingGreetingName: getCurrentUserFirstName() ?? undefined};
  }
  return undefined;
}

// Rendering the title key with an empty date yields the label onboarding titles start with.
function onboardingTitlePrefix(i18n: LocalizationStore): string {
  return i18n.t(`copilot.onboarding.chat-title`, {date: ``}).trim();
}

// The most recently updated conversation whose title starts with the localized onboarding label.
export function findOnboardingConversation(
  conversations: ConversationSummary[],
  i18n: LocalizationStore,
): ConversationSummary | undefined {
  const prefix = onboardingTitlePrefix(i18n);
  if (!prefix) return undefined; // never match everything on a degenerate/empty label
  return (
    conversations
      .filter((c) => c.title.startsWith(prefix))
      // updated_at is ISO-8601, and the service renders every row at the same offset,
      // so the fixed-width fields sort correctly as a string.
      .reduce<ConversationSummary | undefined>(
        (best, c) => (!best || c.updated_at > best.updated_at ? c : best),
        undefined,
      )
  );
}

function toChatIntent(raw: string | null): CopilotChatIntent | undefined {
  const intents = Object.values(CopilotChatIntent) as string[];
  return raw && intents.includes(raw) ? (raw as CopilotChatIntent) : undefined;
}

/** Reads a recognized `chat_intent` from the URL, or undefined when absent/unknown. */
export function parseChatIntent(search: string = window.location.search): CopilotChatIntent | undefined {
  return toChatIntent(new URLSearchParams(search).get(COPILOT_CHAT_INTENT_QUERY_PARAM));
}

export enum CopilotChatView {
  // Normal active chat (the fallback when cp_chat_view is absent or unrecognized).
  Default = `default`,
  // View-only shared snapshot.
  Shared = `shared`,
}

export enum CopilotLoadError {
  Generic = `generic`,
  Private = `private`,
}

export enum CopilotLocation {
  // Side-panel share lives on whatever app page the sharer was on, so the
  // conversation id rides along as a query param (the URL path belongs to that app).
  Panel = `panel`,
  // Full-page share lives on /app/agent/<id>; the id is a route param.
  FullPage = `full-page`,
}

export type PanelQueryParams = {
  /** cp_chat_view — `default` when absent or unrecognized. */
  chatView: CopilotChatView;
  /** cp_conversation_id, if present. */
  conversationId?: string;
  /** cp_num_messages — shared snapshot bound; only the shared view sets it (undefined = all messages). */
  sharedNumMessages?: number;
};

export type FullPageQueryParams = {
  /** cp_chat_view — `default` when absent or unrecognized. */
  chatView: CopilotChatView;
  /** cp_num_messages — shared snapshot bound; only the shared view sets it (undefined = all messages). */
  sharedNumMessages?: number;
};

function parseNonNegInt(raw: string | null): number | undefined {
  // Treat an empty value (`cp_num_messages=`) as absent — Number(``) is 0,
  // which would otherwise turn a mangled link into a zero-message snapshot.
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

function parseChatView(raw: string | null): CopilotChatView {
  const modes = Object.values(CopilotChatView) as string[];
  return raw && modes.includes(raw) ? (raw as CopilotChatView) : CopilotChatView.Default;
}

// The snapshot bound only applies to a shared view; ignored otherwise.
function parseSharedNumMessages(params: URLSearchParams, chatView: CopilotChatView): number | undefined {
  if (chatView !== CopilotChatView.Shared) return undefined;
  return parseNonNegInt(params.get(COPILOT_SHARED_NUM_MESSAGES_QUERY_PARAM));
}

/**
 * Parses copilot query params for the side-panel surface (any app page). The
 * conversation id lives in the query string because the URL path is owned by the
 * host app (insights, boards, etc.).
 */
export function parsePanelQueryParams(search: string = window.location.search): PanelQueryParams {
  const params = new URLSearchParams(search);
  const chatView = parseChatView(params.get(COPILOT_CHAT_VIEW_QUERY_PARAM));
  return {
    chatView,
    conversationId: params.get(COPILOT_CONVERSATION_ID_QUERY_PARAM) ?? undefined,
    sharedNumMessages: parseSharedNumMessages(params, chatView),
  };
}

/**
 * Parses copilot query params for the full-page surface (/app/agent/:id). The
 * conversation id is the route param, supplied separately by the caller — not
 * parsed here.
 */
export function parseFullPageQueryParams(search: string = window.location.search): FullPageQueryParams {
  const params = new URLSearchParams(search);
  const chatView = parseChatView(params.get(COPILOT_CHAT_VIEW_QUERY_PARAM));
  return {
    chatView,
    sharedNumMessages: parseSharedNumMessages(params, chatView),
  };
}

// Centralized so "is a turn currently in flight" can't drift between call sites.
export function isTurnInFlight(status: ChatStatus): boolean {
  return status === `submitted` || status === `streaming`;
}

/**
 * Whether the user has sent a message since the assistant turn that produced
 * `toolCallId`. Cards whose footer actions re-send a prompt use this to offer
 * those actions only until the user replies, the way follow-up chips do.
 * Assistant-only messages after the turn (e.g. a turn-end marker) don't count,
 * and a tool call that isn't in `messages` counts as not replied to.
 */
export function hasUserRepliedSinceToolCall(messages: readonly CopilotUIMessage[], toolCallId: string): boolean {
  const toolCallMessageIndex = messages.findIndex((message) =>
    message.parts.some((part) => `toolCallId` in part && part.toolCallId === toolCallId),
  );
  if (toolCallMessageIndex === -1) {
    return false;
  }
  return messages.slice(toolCallMessageIndex + 1).some((message) => message.role === `user`);
}

export function getCopyableTextFromMessage(message: CopilotUIMessage): string {
  return message.parts
    .map((part) => {
      if (part.type === `text`) {
        return part.text.trim();
      }
      // TODO: Add support for other message part types.
      return ``;
    })
    .filter(Boolean)
    .join(`\n`);
}

/** True anywhere on the full-page surface — the bare `/app/agent` index or a conversation route. */
export function isFullPagePath(): boolean {
  const urlState = StateFromUrlUtils.parseStateFromUrl();
  return getReportName(urlState?.appName) === ReportName.Agent;
}

/** True when the path is a full-page conversation route (`/app/agent/<id>`), not the bare `/app/agent` index. */
export function isFullPageConversationPath(): boolean {
  const urlState = StateFromUrlUtils.parseStateFromUrl();
  return getReportName(urlState?.appName) === ReportName.Agent && !!urlState?.appPath && urlState.appPath !== `/`;
}

export function fullPagePath(): string {
  return getReportUrl(`agent`, {
    projectId: Number(getProjectId()),
    workspaceId: Number(getWorkspaceId()),
  });
}

// Agent deep link that hardcodes the onboarding intent so the agent auto-submits the
// "help me get set up" prompt on arrival. Takes the project id (and optional workspace
// id) explicitly rather than reading getProjectId()/getWorkspaceId() from the URL so
// callers can target a project other than the one the current URL points at.
export function onboardingAgentPath(projectId: number, workspaceId?: number): string {
  return getReportUrl(`agent`, {projectId, workspaceId}, true, {
    [COPILOT_CHAT_INTENT_QUERY_PARAM]: CopilotChatIntent.UserOnboarding,
  });
}

// Bare path, no query string — callers append their own search/hash. ignoreSearch
// drops getReportUrl's persisted params (mp_source, feature-flags, …) so they can't
// land spliced in front of the id.
export function conversationPath(id: string): string {
  return getReportUrl(`agent/${id}`, {projectId: Number(getProjectId()), workspaceId: Number(getWorkspaceId())}, true);
}

// The Mixpanel Agent's own traces self-ingest into internal project 3 (workspace 75)
// on prod-US, whatever project the customer chatted from, so the link always targets
// that project rather than the current route. startedAt is required by the detail page;
// keep the endpoint and param in sync with buildConversationDetailPath in apps/agent-analytics.
const AGENT_TRACE_PROJECT_ID = 3;
const AGENT_TRACE_WORKSPACE_ID = 75;
const AGENT_TRACE_HOST = `https://mixpanel.com`;

export function agentIntelligenceConversationUrl(conversationId: string, startedAtSec: number): string {
  const path = getReportUrl(
    `${ReportName.AgentIntelligence}/conversations/${encodeURIComponent(conversationId)}`,
    {projectId: AGENT_TRACE_PROJECT_ID, workspaceId: AGENT_TRACE_WORKSPACE_ID},
    true,
    {startedAt: String(Math.floor(startedAtSec))},
  );
  return path ? `${AGENT_TRACE_HOST}${path}` : ``;
}

/** Sets cp_chat_view + cp_num_messages; the bound is omitted when unset (= all messages). */
export function setSharedViewParams(params: URLSearchParams, sharedNumMessages?: number): void {
  params.set(COPILOT_CHAT_VIEW_QUERY_PARAM, CopilotChatView.Shared);
  if (Number.isFinite(sharedNumMessages)) {
    params.set(COPILOT_SHARED_NUM_MESSAGES_QUERY_PARAM, String(sharedNumMessages));
  }
}

/** Relative full-page URL for a shared conversation, e.g. `/project/1/app/agent/<id>?cp_chat_view=shared&cp_num_messages=12`. */
export function sharedConversationPath(id: string, sharedNumMessages?: number): string {
  const search = new URLSearchParams();
  setSharedViewParams(search, sharedNumMessages);
  return `${conversationPath(id)}?${search.toString()}`;
}

export function getCopilotURLParams() {
  const dict = omitBy(
    {
      project_id: getProjectId() || undefined,
      workspace_id: getWorkspaceId() || undefined,
    },
    isNil,
  );

  const urlParams = new URLSearchParams(dict as Record<string, string>);

  return urlParams.toString();
}
